import { ApplicationConfig, provideBrowserGlobalErrorListeners, ErrorHandler, APP_INITIALIZER, inject, Provider } from '@angular/core';
import { provideRouter, withComponentInputBinding, withRouterConfig, withNavigationErrorHandler, Router, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { tap, map, Observable } from 'rxjs';
import * as Sentry from "@sentry/angular";
import { routes } from './app.routes';
import { apiInterceptor } from './interceptors/api.interceptor';
import { environment } from '../environments/environment';
import { AuthService, User } from '../../../shared/src/services/auth.service';
import { StaffEventService } from '../../../shared/src/services/staff-event.service';
import { INAPP_BROWSER_IGNORE_ERRORS } from '../../../shared/src/utils/sentry-ignore';
import { handleChunkLoadNavigationError } from '../../../shared/src/utils/chunk-reload';

const sentryProviders: Provider[] = [];

if (environment.sentryDsnStaff) {
  Sentry.init({
    dsn: environment.sentryDsnStaff,
    environment: environment.sentryEnvironment,
    // #583: drop errors thrown by in-app browser injected scripts (Meta) —
    // not our code, pure quota noise. Patterns live in the shared util.
    ignoreErrors: INAPP_BROWSER_IGNORE_ERRORS,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay disabled — re-enable by adding Sentry.replayIntegration() and
      // setting replaysSessionSampleRate / replaysOnErrorSampleRate below.
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ["localhost", /^https:\/\/api(-staging)?\.jidvei\.ro/],
  });

  sentryProviders.push(
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      useClass: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => inject(Sentry.TraceService),
      multi: true,
    }
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...sentryProviders,
    provideBrowserGlobalErrorListeners(),
    provideRouter(routes, withComponentInputBinding(), withInMemoryScrolling({
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'enabled',
    }), withRouterConfig({
      onSameUrlNavigation: 'reload'
    }),
    // #623: a lazy chunk that vanished after a deploy is recovered with one
    // reload. A router feature, NOT an ErrorHandler provider — that would
    // replace Sentry's handler above and silence error reporting.
    withNavigationErrorHandler(handleChunkLoadNavigationError)),
    provideHttpClient(
      withInterceptors([apiInterceptor])
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const authService = inject(AuthService);
        const staffEventService = inject(StaffEventService);
        return () => {
          return authService.initialize().pipe(
            tap((user: User | null) => {
              if (user) {
                staffEventService.initialize();
              }
            })
          );
        };
      },
      multi: true
    }
  ]
};
